import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PasswordInput, type PasswordInputProps } from '@mantine/core';
import { IconEye, IconEyeOff } from '@tabler/icons-react';

// Иконка-глаз: открыт, когда пароль виден; закрыт (перечёркнут), когда скрыт.
function VisibilityIcon({ reveal }: { readonly reveal: boolean }) {
  return reveal ? <IconEye size={18} stroke={1.6} /> : <IconEyeOff size={18} stroke={1.6} />;
}

/**
 * PasswordInput с кнопкой-«глазом» справа: клик переключает видимость пароля,
 * иконка открывается/закрывается. aria-label меняется в зависимости от состояния.
 * Принимает все props PasswordInput (включая spread из form.getInputProps).
 */
export default function PasswordField(props: PasswordInputProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  return (
    <PasswordInput
      {...props}
      visible={visible}
      onVisibilityChange={setVisible}
      visibilityToggleIcon={VisibilityIcon}
      aria-invalid={props.error ? true : undefined}
      visibilityToggleButtonProps={{
        // Mantine по умолчанию убирает кнопку из обхода клавишей Tab, и показать пароль с клавиатуры нельзя.
        tabIndex: 0,
        // Mantine переключает видимость по нажатию мыши, касанию и пробелу. Enter и вспомогательные технологии дают только
        // click с detail 0: обычный щелчок мышью сюда не попадает, его уже обработал mousedown.
        onClick: (event: MouseEvent<HTMLButtonElement>) => {
          if (event.detail === 0) {
            setVisible((current) => !current);
          }
        },
        'aria-label': visible ? t('auth.password.hide') : t('auth.password.show'),
      }}
    />
  );
}
